import axios, { AxiosError } from "axios";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { AuthContext } from "../../../context/AuthContext";
import { AdminEndpointEnum } from "../../../enums/endpoints";
import { ADMIN_API_URL } from "../../../utils/env";
import { ToastEnum, ToastStatusEnum, useToast } from "../../toast";

export interface IDailyKillSwitchStatus {
  tripped: boolean;
  used: string;
  threshold: string;
  reset_policy_enabled?: boolean;
  activation_count?: number;
  trip_id?: string;
  required_approvals?: number;
  approvals?: { email: string; approved_at: string }[];
  has_approved?: boolean;
  can_approve?: boolean;
}

export type KillSwitchResetOutcome = "reset" | "approval_recorded";

const hasApprovalState = (status: IDailyKillSwitchStatus) =>
  typeof status.trip_id === "string" &&
  /^\d+$/.test(status.trip_id) &&
  Number.isInteger(status.activation_count) &&
  status.activation_count! >= 0 &&
  status.activation_count! <= 4 &&
  (status.required_approvals === 1 || status.required_approvals === 2) &&
  Array.isArray(status.approvals) &&
  status.approvals.length < status.required_approvals &&
  typeof status.has_approved === "boolean" &&
  typeof status.can_approve === "boolean";

export const useKillSwitch = () => {
  const { queryConfig, tokenId } = useContext(AuthContext);
  const { addToast } = useToast();
  const [isMutating, setIsMutating] = useState(false);
  const [awaitingPolicy, setAwaitingPolicy] = useState(false);
  const inFlight = useRef(false);
  const currentToken = useRef(tokenId);
  currentToken.current = tokenId;

  const endpoint = ADMIN_API_URL(AdminEndpointEnum.DAILY_KILL_SWITCH);
  const swr = useSWR<IDailyKillSwitchStatus, AxiosError>(
    tokenId ? [endpoint, tokenId] : undefined,
    async () => {
      const response = await axios.get<IDailyKillSwitchStatus>(
        endpoint,
        queryConfig()
      );
      return response.data;
    },
    { refreshInterval: 10000 }
  );

  useEffect(() => {
    if (swr.data?.reset_policy_enabled === true) setAwaitingPolicy(false);
  }, [swr.data?.reset_policy_enabled]);
  useEffect(() => setAwaitingPolicy(false), [tokenId]);

  const toast = useCallback(
    (header: string, subheader: string, failed = false) => {
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header,
          subheader,
          status: failed ? ToastStatusEnum.ERROR : ToastStatusEnum.SUCCESS,
        },
        5000
      );
    },
    [addToast]
  );

  const reportError = useCallback(
    (error: unknown, header: string) => {
      const response = (error as AxiosError<{ error?: string }>)?.response;
      toast(
        header,
        response?.data?.error ||
          (error as Error)?.message ||
          "Please refresh and try again.",
        true
      );
    },
    [toast]
  );

  const resetDailyKillSwitch =
    useCallback(async (): Promise<KillSwitchResetOutcome | null> => {
      if (inFlight.current) return null;
      if (!tokenId) {
        toast(
          "Sign in required",
          "Sign in with your staff account to reset the kill switch.",
          true
        );
        return null;
      }
      const status = swr.data;
      if (
        !status ||
        swr.error ||
        swr.isLoading ||
        (status.reset_policy_enabled &&
          (!hasApprovalState(status) ||
            !status.tripped ||
            !status.can_approve ||
            status.has_approved))
      ) {
        toast(
          "Reset unavailable",
          "Refresh the kill-switch status before continuing.",
          true
        );
        return null;
      }
      inFlight.current = true;
      setIsMutating(true);
      try {
        const { data } = await axios.post(
          ADMIN_API_URL(AdminEndpointEnum.RESET_DAILY_KILL_SWITCH),
          status.trip_id ? { trip_id: status.trip_id } : {},
          {
            ...queryConfig(),
            headers: {
              ...queryConfig().headers,
              Authorization: `Bearer ${tokenId}`,
            },
          }
        );
        if (currentToken.current !== tokenId) return null;
        const outcome: KillSwitchResetOutcome | undefined =
          data?.outcome ??
          (!status.reset_policy_enabled && data?.success === true
            ? "reset"
            : undefined);
        if (
          data?.success !== true ||
          (outcome !== "reset" && outcome !== "approval_recorded")
        ) {
          throw new Error(
            "The backend did not confirm this reset. Refresh to check its status."
          );
        }
        // Never announce a reset for the first approval: the switch remains engaged.
        toast(
          outcome === "reset" ? "Kill switch reset" : "Approval recorded",
          outcome === "reset"
            ? "Review flagged withdrawals separately; their manual-review status is unchanged."
            : "The kill switch stays on until a different staff member approves."
        );
        swr.mutate();
        return outcome;
      } catch (error) {
        if (currentToken.current === tokenId) {
          reportError(error, "Reset not completed");
          swr.mutate();
        }
        return null;
      } finally {
        inFlight.current = false;
        setIsMutating(false);
      }
    }, [tokenId, swr, toast, reportError, queryConfig]);

  const enableResetApprovals = useCallback(async (): Promise<boolean> => {
    if (
      inFlight.current ||
      awaitingPolicy ||
      !tokenId ||
      swr.error ||
      swr.isLoading ||
      swr.isValidating ||
      swr.data?.reset_policy_enabled !== false ||
      !hasApprovalState(swr.data)
    )
      return false;
    inFlight.current = true;
    setIsMutating(true);
    try {
      const { data } = await axios.post(
        ADMIN_API_URL("daily-kill-switch/reset-approvals/enable"),
        {},
        queryConfig()
      );
      if (currentToken.current !== tokenId) return false;
      if (
        data?.success !== true ||
        data?.reset_policy_enabled !== true ||
        data?.outcome !== "policy_enabled"
      )
        throw new Error(
          "The backend did not confirm policy activation. Refresh status before retrying."
        );
      setAwaitingPolicy(true);
      swr.mutate();
      return true;
    } catch (error) {
      if (currentToken.current === tokenId) {
        reportError(error, "Policy activation not confirmed");
        swr.mutate();
      }
      return false;
    } finally {
      inFlight.current = false;
      setIsMutating(false);
    }
  }, [awaitingPolicy, tokenId, swr, queryConfig, reportError]);

  const setDailyKillSwitchThreshold = useCallback(
    async (threshold: number): Promise<boolean> => {
      if (inFlight.current) return false;
      if (!tokenId || !Number.isFinite(threshold) || threshold <= 0)
        return false;
      inFlight.current = true;
      setIsMutating(true);
      try {
        const { data } = await axios.post(
          ADMIN_API_URL(AdminEndpointEnum.DAILY_KILL_SWITCH_THRESHOLD),
          { threshold },
          {
            ...queryConfig(),
            headers: {
              ...queryConfig().headers,
              Authorization: `Bearer ${tokenId}`,
            },
          }
        );
        if (currentToken.current !== tokenId) return false;
        if (data?.success !== true)
          throw new Error("The backend did not confirm this threshold change.");
        toast("Threshold updated", `New cap: $${threshold.toLocaleString()}`);
        swr.mutate();
        return true;
      } catch (error) {
        if (currentToken.current === tokenId) {
          reportError(error, "Threshold not changed");
          swr.mutate();
        }
        return false;
      } finally {
        inFlight.current = false;
        setIsMutating(false);
      }
    },
    [tokenId, swr, toast, reportError, queryConfig]
  );

  return {
    ...swr,
    data: tokenId ? swr.data : undefined,
    isSignedIn: Boolean(tokenId),
    canReset: Boolean(
      tokenId &&
        swr.data &&
        !swr.error &&
        !swr.isLoading &&
        (!swr.data.reset_policy_enabled ||
          (hasApprovalState(swr.data) &&
            swr.data.tripped &&
            swr.data.can_approve &&
            !swr.data.has_approved))
    ),
    canEnablePolicy: Boolean(
      tokenId &&
        !swr.error &&
        !swr.isLoading &&
        !swr.isValidating &&
        !awaitingPolicy &&
        swr.data?.reset_policy_enabled === false &&
        hasApprovalState(swr.data)
    ),
    awaitingPolicy,
    enableResetApprovals,
    isMutating,
    resetDailyKillSwitch,
    setDailyKillSwitchThreshold,
  };
};
